/** Open once, relaxing unsupported constraints without retrying a permission denial. */
export async function requestMicrophoneStream(deviceId = null) {
  if (!globalThis.navigator?.mediaDevices?.getUserMedia) {
    throw Object.assign(new Error("This browser cannot use a microphone. Try an updated browser."), { name: "NotSupportedError" });
  }
  const preferred = { channelCount: 1, echoCancellation: true, noiseSuppression: true, latency: 0 };
  const attempts = deviceId ? [
    { ...preferred, deviceId: { exact: deviceId } }, { deviceId: { exact: deviceId } }, preferred, true,
  ] : [preferred, true];
  let lastError;
  for (const audio of attempts) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio });
      return { stream, attemptLabel: audio === true ? "default device" : "speech constraints" };
    } catch (error) {
      if (!["OverconstrainedError", "ConstraintNotSatisfiedError", "NotFoundError"].includes(error?.name)) throw error;
      lastError = error;
    }
  }
  throw lastError;
}

export function microphoneErrorMessage(error) {
  if (["NotAllowedError", "PermissionDeniedError", "SecurityError"].includes(error?.name)) {
    return "Microphone access is blocked. Allow it in your browser's site settings, then try again.";
  }
  if (error?.name === "NotFoundError") return "No microphone was found. Connect one and try again.";
  if (error?.name === "NotReadableError") return "The microphone is unavailable. Close other apps using it and try again.";
  if (["OverconstrainedError", "ConstraintNotSatisfiedError"].includes(error?.name)) return "The selected microphone is unavailable. Choose another microphone.";
  if (error?.name === "MicrophoneBusyError") return "The microphone is still starting. Try again in a moment.";
  if (error?.name === "NotSupportedError") return "This browser cannot capture live audio. Try an updated browser.";
  return error?.message || "Unable to start the microphone. Please try again.";
}

export function microphoneSupportError() {
  if (!globalThis.navigator?.mediaDevices?.getUserMedia ||
    !(globalThis.AudioContext || globalThis.webkitAudioContext) ||
    typeof AudioWorkletNode === "undefined") {
    return "Microphone dictation is not supported in this browser. Try an updated browser.";
  }
  return null;
}
